import { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/cn';

export interface PriceScannerProps {
  /** Reference index, 1..100 — known before the bet. */
  current: number;
  /** The rolled outcome — known before the animation starts. */
  target: number | null;
  guess: 'higher' | 'lower' | null;
  won: boolean | null;
  spinning: boolean;
  /** Fires the moment the result becomes visible — hook a win celebration here. */
  onReveal?: () => void;
  /**
   * Fires after the result has had a moment to be read. This is the signal
   * that it's safe to roll a new `current` for the next round — doing that
   * any earlier (e.g. in `onReveal`) changes the reference number in the
   * same frame the win/loss flash appears, which reads as the gauge glitching
   * rather than updating.
   */
  onSettled?: () => void;
}

/** How long the result stays on screen, flashed, before the round is considered over. */
const HOLD_MS = 1300;

/**
 * A ticking price readout plus a gauge that tells the two-part story:
 * "which zone were you betting on" and "where did it actually land".
 *
 * The digits cycle fast (classic slot-style suspense) before locking onto the
 * true, already-known result; the marker then glides into its resting
 * position on the gauge so the win/loss reads spatially, not just numerically.
 */
export function PriceScanner({
  current,
  target,
  guess,
  won,
  spinning,
  onReveal,
  onSettled,
}: PriceScannerProps) {
  const [displayed, setDisplayed] = useState(current);
  const [markerAt, setMarkerAt] = useState(current);
  const [markerVisible, setMarkerVisible] = useState(false);
  const [flash, setFlash] = useState<'idle' | 'won' | 'lost'>('idle');
  const spinToken = useRef(0);

  // A fresh `current` while idle means the caller just rolled a new
  // reference for the next round — clear the previous round's marker/flash
  // instead of leaving them stranded next to a number they no longer relate
  // to. Guarded on `spinning` so this never fires mid-round (target changes
  // together with a fresh `current` only between rounds, never during one).
  useEffect(() => {
    if (spinning) return;
    setMarkerVisible(false);
    setFlash('idle');
    setDisplayed(current);
    setMarkerAt(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  useEffect(() => {
    if (!spinning || target === null) return;
    const token = ++spinToken.current;
    setFlash('idle');
    setMarkerVisible(false);

    const interval = window.setInterval(() => {
      setDisplayed(1 + Math.floor(Math.random() * 100));
    }, 55);

    const stopTicking = window.setTimeout(() => {
      if (spinToken.current !== token) return;
      window.clearInterval(interval);
      setDisplayed(target);
      // Marker is already mounted (at the previous resting spot) — moving
      // `left` now, one frame after `markerVisible` flips true, is what
      // actually gives the CSS transition something to animate between.
      setMarkerAt(target);
      requestAnimationFrame(() => setMarkerVisible(true));
    }, 1100);

    const revealTimer = window.setTimeout(() => {
      if (spinToken.current !== token) return;
      setFlash(won ? 'won' : 'lost');
      onReveal?.();
    }, 1100 + 600);

    const settleTimer = window.setTimeout(() => {
      if (spinToken.current !== token) return;
      onSettled?.();
    }, 1100 + 600 + HOLD_MS);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(stopTicking);
      window.clearTimeout(revealTimer);
      window.clearTimeout(settleTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinning, target]);

  return (
    <div className="flex w-full max-w-sm flex-col items-center gap-3">
      <div
        data-sg-numeric
        className={cn(
          'text-readout-lg font-bold transition-colors',
          flash === 'won' ? 'text-success' : flash === 'lost' ? 'text-danger' : 'text-silicate',
        )}
      >
        {displayed}
      </div>

      {/* Gauge: 1..100 left to right. The half the player bet on glows faintly. */}
      <div className="relative h-3 w-full overflow-visible rounded-pill bg-surface-2">
        <div
          className={cn(
            'absolute inset-y-0 left-0 rounded-l-pill transition-[width] duration-500 ease-hud',
            guess === 'lower' ? 'bg-silicate/25' : 'bg-transparent',
          )}
          style={{ width: `${current}%` }}
        />
        <div
          className={cn(
            'absolute inset-y-0 right-0 rounded-r-pill transition-[width] duration-500 ease-hud',
            guess === 'higher' ? 'bg-silicate/25' : 'bg-transparent',
          )}
          style={{ width: `${100 - current}%` }}
        />

        {/* Reference marker — where "current" sits. Glides to a new spot between rounds. */}
        <div
          className="absolute top-1/2 h-4 w-0.5 -translate-y-1/2 bg-content-muted transition-[left] duration-500 ease-hud"
          style={{ left: `${current}%` }}
          aria-hidden
        />

        {/*
          Outcome marker — always mounted (parked on the reference spot) so the
          `left` transition has a real starting point to animate from once a
          result lands; a marker that only mounts at its final position has
          nothing to glide from and just pops into place.
        */}
        <div
          className={cn(
            'absolute top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 shadow-glow-sm',
            'transition-[left,opacity] duration-500 ease-hud',
            markerVisible ? 'opacity-100' : 'opacity-0',
            flash === 'won'
              ? 'border-success bg-success/30'
              : flash === 'lost'
                ? 'border-danger bg-danger/30'
                : 'border-content bg-content/30',
          )}
          style={{ left: `${markerAt}%` }}
          aria-hidden
        />
      </div>

      <div className="flex w-full justify-between text-label text-content-muted">
        <span>1</span>
        <span data-sg-numeric>Referenz {current}</span>
        <span>100</span>
      </div>
    </div>
  );
}
